/**
 * How a map begins.
 *
 * It used to be a button, and a button is the right control when there is one answer. There are
 * three: the Builder (a blank map, arranged the way you choose), a template, and a generated one.
 * Three answers behind a button is a menu that hides two of them; three answers in a dialog is a
 * choice.
 *
 * The AI tab was rendered and disabled for two phases, with the reason on it, which was a
 * deliberate reading of "a tab that opens onto nothing is worse than a tab that is not there":
 * *opening* onto nothing is the failure. Step 33 enabled it rather than inventing it. It is still
 * disabled for a browser with no account, because generating is metered against one — and the
 * panel behind it says so rather than offering a button that always fails.
 *
 * Every preview is rendered from its own template — see `templatePreview.ts` — so this screen
 * cannot promise a map the product does not make.
 */
import * as Tabs from '@radix-ui/react-tabs';
import { useMemo, useState } from 'react';

import {
  bundledTemplates,
  groupByCategory,
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_CATEGORY_LABELS_PT,
  type Template,
  type TemplateCategory,
} from '@orbit-mind/template-catalog';

import type { MapDocument } from '@orbit-mind/domain';

import { useCanvasFontFamily } from '../canvas/measurer';
import type { LayoutId } from '@orbit-mind/shared-types';

import { BuilderPanel } from './BuilderPanel';
import { GeneratePanel } from './GeneratePanel';
import { TextImportPanel } from './TextImportPanel';
import { useLocale } from '@orbit-mind/engine-react';
import { Modal } from '../ui/Modal';
import { templatePreviewSrc } from './templatePreview';

export interface CreateMapDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** A blank map arranged by the layout chosen in the Builder, named, or "Untitled map". */
  readonly onCreateBlank: (layoutId: LayoutId, title: string) => void;
  readonly onCreateFromTemplate: (template: Template) => void;
  /** Accepting a generated draft. The document is the one the preview showed, untouched. */
  readonly onCreateGenerated: (document: MapDocument) => void;
  /** False with no account: generating is metered against one, so the tab says so instead. */
  readonly generateAvailable: boolean;
  /** True while a map is being written, so the dialog cannot be asked twice. */
  readonly busy: boolean;
}

/**
 * Read once, at module scope.
 *
 * The catalogue is bundled data and cannot change while the application is running, so building
 * the grouping per render would be work with a guaranteed identical answer — and a new array
 * identity every render, which is the thing that makes a memo downstream useless.
 */
const GROUPS = groupByCategory(bundledTemplates());

export function CreateMapDialog({
  open,
  onOpenChange,
  onCreateBlank,
  onCreateFromTemplate,
  onCreateGenerated,
  generateAvailable,
  busy,
}: CreateMapDialogProps): React.JSX.Element {
  const { text: t } = useLocale();
  const [category, setCategory] = useState<TemplateCategory>(GROUPS[0]?.category ?? 'ideas');

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('New map', 'Novo mapa')}
      description={t(
        'Give your ideas a useful starting point.',
        'Dê às suas ideias um ponto de partida útil.',
      )}
      autoFocusSelector="[data-create-blank]"
    >
      <Tabs.Root defaultValue="builder" className="flex min-h-0 flex-1 flex-col">
        <Tabs.List
          aria-label={t('How to start', 'Como começar')}
          className="border-edge flex shrink-0 gap-1 border-b px-2 sm:px-4"
        >
          <Tab value="builder">{t('Blank', 'Em branco')}</Tab>
          <Tab value="text">{t('From text', 'Do texto')}</Tab>
          <Tab value="templates">{t('Templates', 'Modelos')}</Tab>
          <Tab
            value="ai"
            disabled={!generateAvailable}
            {...(generateAvailable ? {} : { hint: 'Needs an account' })}
          >
            AI
          </Tab>
        </Tabs.List>

        <Tabs.Content value="builder" className="flex min-h-0 flex-1 flex-col outline-none">
          <BuilderPanel onCreate={onCreateBlank} busy={busy} />
        </Tabs.Content>

        <Tabs.Content value="templates" className="flex min-h-0 flex-1 flex-col outline-none">
          <TemplatePanel
            category={category}
            onCategory={setCategory}
            onChoose={onCreateFromTemplate}
            busy={busy}
          />
        </Tabs.Content>

        <Tabs.Content value="text" className="flex min-h-0 flex-1 flex-col outline-none">
          <TextImportPanel onAccept={onCreateGenerated} busy={busy} />
        </Tabs.Content>
        <Tabs.Content value="ai" className="flex min-h-0 flex-1 flex-col outline-none">
          <GeneratePanel available={generateAvailable} busy={busy} onAccept={onCreateGenerated} />
        </Tabs.Content>
      </Tabs.Root>
    </Modal>
  );
}

function Tab({
  value,
  children,
  disabled = false,
  hint,
}: {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  hint?: string;
}): React.JSX.Element {
  return (
    <Tabs.Trigger
      value={value}
      disabled={disabled}
      {...(hint === undefined ? {} : { title: hint, 'aria-description': hint })}
      className="text-ink-muted data-[state=active]:border-accent data-[state=active]:text-ink hover:text-ink -mb-px border-b-2 border-transparent px-3 py-2.5 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </Tabs.Trigger>
  );
}

function TemplatePanel({
  category,
  onCategory,
  onChoose,
  busy,
}: {
  category: TemplateCategory;
  onCategory: (category: TemplateCategory) => void;
  onChoose: (template: Template) => void;
  busy: boolean;
}): React.JSX.Element {
  const { text: t } = useLocale();
  const shown = GROUPS.find((group) => group.category === category)?.templates ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/*
        A scrolling row rather than a wrapped block: eight categories wrapped to three lines on a
        phone push the grid — the thing somebody came here to look at — off the screen.
      */}
      <div
        role="group"
        aria-label="Categories"
        className="border-edge flex shrink-0 gap-1.5 overflow-x-auto border-b px-3 py-2.5 sm:px-4"
      >
        {GROUPS.map((group) => (
          <button
            key={group.category}
            type="button"
            aria-pressed={group.category === category}
            onClick={() => {
              onCategory(group.category);
            }}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              group.category === category
                ? 'border-accent bg-accent-soft text-accent'
                : 'border-edge text-ink-muted hover:border-edge-strong hover:text-ink'
            }`}
          >
            {t(
              TEMPLATE_CATEGORY_LABELS[group.category],
              TEMPLATE_CATEGORY_LABELS_PT[group.category],
            )}
          </button>
        ))}
      </div>

      <ul className="grid min-h-0 flex-1 content-start gap-3 overflow-y-auto overscroll-contain p-3 sm:grid-cols-2 sm:p-4">
        {shown.map((template) => (
          <TemplateCard key={template.id} template={template} busy={busy} onChoose={onChoose} />
        ))}
      </ul>
    </div>
  );
}

function TemplateCard({
  template,
  busy,
  onChoose,
}: {
  template: Template;
  busy: boolean;
  onChoose: (template: Template) => void;
}): React.JSX.Element {
  const fontFamily = useCanvasFontFamily();
  // Keyed by the font as well as the template: node boxes are sized by measuring their text, so
  // the preview drawn before Inter arrives and the one drawn after are different pictures.
  const preview = useMemo(
    () => templatePreviewSrc(template.document, fontFamily),
    [template.document, fontFamily],
  );

  return (
    <li>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          onChoose(template);
        }}
        className="rounded-panel border-edge bg-surface hover:border-accent focus-visible:border-accent group flex w-full flex-col overflow-hidden border text-left transition-[border-color,box-shadow] hover:shadow-md disabled:opacity-50"
      >
        {/*
          A fixed pale backdrop in both themes, rather than a surface token.
          
          The preview is a rendering of a map on paper — `sceneToSvg` writes dark text on light
          boxes, which is what the SVG and PDF exports of any map look like. Sitting that on a
          near-black card in the dark theme reads as a rendering fault; sitting it on a sheet reads
          as a thumbnail of a document, which is what it is.
        */}
        <span
          className="block h-36 w-full overflow-hidden sm:h-40"
          style={{ backgroundColor: '#f4f5f8' }}
        >
          {/*
            Empty `alt`, on purpose. The preview is a picture of the map whose name and description
            are read out immediately below it, so describing it again would announce the same
            template twice to a screen reader and tell it nothing the words do not.
          */}
          <img
            src={preview}
            alt=""
            className="h-full w-full object-contain p-2 transition-transform duration-200 group-hover:scale-[1.02]"
          />
        </span>
        <span className="block px-3.5 py-3">
          <span className="block text-sm font-medium">{template.name}</span>
          <span className="text-ink-muted mt-0.5 block text-xs">{template.description}</span>
        </span>
      </button>
    </li>
  );
}
